import React, { useState, useEffect } from 'react';
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import auth from './auth0';
import { FeatureFlags } from '../constants/featureFlags';
import logger from '../utils/logger';

const PermissionsContext = React.createContext(null);

/**
 * Update GrowthBook user attributes with current authentication state
 * This ensures feature flags can be properly targeted based on user context
 */
const updateGrowthBookAttributes = (growthbook) => {
  if (!growthbook) return;
  
  const userEmail = localStorage.getItem('AUTH_USER_EMAIL');
  const userSub = localStorage.getItem('AUTH_USER_SUB');
  const userRole = localStorage.getItem('AUTH_PACIFY_ROLE');
  const userProfile = localStorage.getItem('AUTH_USER_PROFILE');
  
  let parsedProfile = {};
  try {
    parsedProfile = JSON.parse(userProfile || '{}');
  } catch (e) {
    logger.log('[PermissionsProvider] Failed to parse user profile:', e);
  }
  
  // Extract user ID from Auth0 sub (format: "auth0|123456")
  const userId = userSub ? userSub.split('|')[1] || userSub : 'anonymous';
  
  const attributes = {
    id: userId,
    email: userEmail || '',
    role_id: userRole ? parseInt(userRole) : null,
    auth0_sub: userSub || '',
    environment: import.meta.env.MODE,
    // Add organization ID if available from profile
    org_id: parsedProfile.organization_id || parsedProfile.org_id || null,
  };
  
  logger.log('[PermissionsProvider] Updating GrowthBook user attributes:', attributes);
  growthbook.setAttributes(attributes);
};

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(auth.getPermissions());
  const [isLoading, setIsLoading] = useState(true);
  const growthbook = useGrowthBook();
  const isRBACEnabled = useFeatureIsOn(FeatureFlags.ENABLE_ADMIN_PANEL_RBAC);
  
  // Wait for GrowthBook to be ready
  const isGrowthBookReady = growthbook?.ready || false;
  
  /**
   * Load permissions on mount and wait for GrowthBook
   * - First, get permissions from localStorage (synchronous)
   * - If empty and user is authenticated, trigger immediate backend fetch
   * - Wait for GrowthBook to be ready before checking RBAC flag
   * - This ensures permissions are always available before rendering
   */
  useEffect(() => {
    // Don't proceed until GrowthBook is ready
    if (!isGrowthBookReady) {
      return;
    }

    const loadPermissions = async () => {
      let currentPermissions = auth.getPermissions();

      // ALWAYS fetch permissions from backend if user is authenticated
      // This ensures we get the full set of permissions from the database,
      // not just the limited set from the JWT (which may be truncated due to token size limits)
      if (auth.isAuthenticated()) {
        try {
          await auth.fetchAndStorePermissions();
          currentPermissions = auth.getPermissions();
          
          // Update GrowthBook user attributes now that we have authenticated user context
          // This ensures feature flags can be properly targeted
          updateGrowthBookAttributes(growthbook);
        } catch (error) {
          console.error('[PermissionsProvider] Failed to fetch permissions:', error);
          // Fall back to JWT permissions if backend fetch fails
          console.warn('[PermissionsProvider] Falling back to JWT permissions');
        }
      }

      setPermissions(currentPermissions);
      setIsLoading(false);
    };

    loadPermissions();
  }, [isGrowthBookReady, isRBACEnabled]);
  
  /**
   * Check if user has a specific permission
   * @param {string} requiredPermission - The permission to check (e.g., 'admin:view-users')
   * @returns {boolean} - True if user has permission OR if RBAC is disabled
   * 
   * When RBAC is disabled, all checks return true (backward compatibility)
   * When RBAC is enabled, checks actual permissions from Auth0
   */
  const hasPermission = (requiredPermission) => {
    // If RBAC is disabled, allow all access (backward compatibility)
    if (!isRBACEnabled) {
      return true;
    }
    
    // If RBAC is enabled, check actual permissions
    return permissions.includes(requiredPermission);
  };

  // Show loading state while permissions are being loaded (only if RBAC is enabled)
  if (isLoading && isRBACEnabled) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading permissions...
      </div>
    );
  }

  return (
    <PermissionsContext.Provider value={{ permissions, hasPermission, isRBACEnabled, isLoading }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = React.useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
